import React from 'react';

type WhereItemProps = {
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  text: string;
  onClick: () => void;
  searchTerm?: string;
};

const WhereItem = ({ icon: Icon, text, onClick, searchTerm }: WhereItemProps) => {
  /**
   * @description
   * 텍스트 내에서 검색어와 일치하는 부분을 찾아 primary 컬러로 하이라이트 처리합니다.
   * 대소문자 구분 없이 매칭하며, 정규표현식 특수문자가 포함된 검색어도 안전하게 처리합니다.
   *
   * @param text - 하이라이트를 적용할 원본 텍스트
   * @param searchTerm - 검색어. 이 값과 일치하는 부분이 하이라이트됩니다.
   *
   * @returns 하이라이트가 적용된 React 엘리먼트
   *
   * @example
   * ```tsx
   *
   * // 검색어가 있는 경우
   * highlightText('Seoul National University', 'seoul')
   * // 결과: <span><span className="text-primary">Seoul</span> National University</span>
   *
   * // 여러 매칭이 있는 경우
   * highlightText('Seoul Station near Seoul Tower', 'Seoul')
   * // 결과: 두 개의 "Seoul"이 모두 하이라이트됨
   * ```
   */
  const highlightText = (text: string, searchTerm?: string) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return <span>{text}</span>;
    }

    /**
     * 검색어에 정규표현식 특수문자가 포함되어 있으면 에러가 발생할 수 있으므로
     * 모든 특수문자 앞에 백슬래시(\)를 추가하여 리터럴 문자로 처리
     * 예: "Seoul (Main)" -> "Seoul \\(Main\\)"
     */
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    /**
     * 대소문자 구분 없이 검색어와 일치하는 모든 부분을 찾는 정규표현식 생성
     */
    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');

    /**
     * 정규표현식으로 텍스트를 분할하여 매칭되는 부분과 그렇지 않은 부분을 분리
     * 예: "Seoul University" + /Seoul/gi -> ["", "Seoul", " University"]
     */
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, index) => {
          const isMatch = part.toLowerCase() === searchTerm.toLowerCase();

          return isMatch ? (
            <span key={index} className="text-primary">
              {part}
            </span>
          ) : (
            <span key={index}>{part}</span>
          );
        })}
      </span>
    );
  };

  return (
    <button
      className="flex w-full items-center gap-x-3 rounded-lg px-0 py-3 text-left text-[14px] text-gray-50 hover:bg-[#F7F7F7] md:gap-x-[14px] md:px-1 md:py-1.25 md:text-c2"
      onClick={onClick}
    >
      <Icon className="h-5 w-5 shrink-0 md:h-5 md:w-5" />
      {highlightText(text, searchTerm)}
    </button>
  );
};

export default WhereItem;
